import { getFeedbackLabelIcon } from '@modules/feedback/components/FeedbackCard/utils/getFeedbackLabelIcon';
import { useIsFeedbackRedesignEnabled } from '@modules/feedbackV2/common/hooks';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { alpha, Box, IconButton, Stack, Typography } from '@mui/material';
import { PermissionOperation, PermissionResource } from 'generated/graphql';
import { ReasonOrEntityLabelType } from 'v2/core/entities/feedback/labels';
import { CanAccess } from '../CanAccess';

export interface PredictionLabelProps {
  label: ReasonOrEntityLabelType;
  isSelected?: boolean;
  isHovered?: boolean;
  size?: 'small' | 'large';
  onEditToggle?: (event: React.MouseEvent) => void;
  isEditable?: boolean;
  isClickable?: boolean;
  subTitle?: string;
  shouldHideVisibilityIcon?: boolean;
}

export const PredictionLabel = ({
  label,
  isSelected = false,
  isHovered = false,
  size = 'large',
  onEditToggle,
  isEditable = false,
  isClickable = false,
  subTitle,
  shouldHideVisibilityIcon = false,
}: PredictionLabelProps) => {
  const isFeedbackRedesignEnabled = useIsFeedbackRedesignEnabled();

  const isSmall = size === 'small';

  const shouldShowEditIcon = isEditable && isHovered;

  const renderVisibilityIcon = () => {
    if (!isClickable || shouldHideVisibilityIcon) {
      return null;
    }

    if (isHovered || isSelected) {
      return (
        <VisibilityRoundedIcon
          color={isSelected ? 'primary' : undefined}
          sx={{
            color: (theme) =>
              isSelected ? undefined : alpha(theme.palette.action.active, 0.56),
            width: '18px',
            height: '18px',
          }}
        />
      );
    }

    return (
      <Box
        sx={{
          width: '18px',
          height: '18px',
        }}
      />
    );
  };

  const renderEditIcon = () => {
    if (!isEditable) {
      return null;
    }

    if (isHovered) {
      return (
        <CanAccess
          resource={PermissionResource.Prediction}
          operation={PermissionOperation.Write}
          tooltipPlacement="top"
        >
          <IconButton size="small" onClick={onEditToggle}>
            <EditRoundedIcon
              sx={{
                color: (theme) => alpha(theme.palette.action.active, 0.56),
                width: '14px',
                height: '14px',
              }}
            />
          </IconButton>
        </CanAccess>
      );
    }

    return (
      <Box
        sx={{
          width: '24px',
          height: '24px',
        }}
      />
    );
  };

  const getTypographyVariant = () => {
    if (isSmall) {
      return 'caption';
    }
    return isFeedbackRedesignEnabled ? 'body2' : 'body1';
  };

  return (
    <Stack
      width="100%"
      direction="row"
      spacing={1}
      alignItems="center"
      justifyContent="space-between"
    >
      <Stack direction="row" spacing={1} alignItems="start">
        {getFeedbackLabelIcon({
          label,
          fontSize: isSmall || isFeedbackRedesignEnabled ? 'small' : 'medium',
          shouldShowPrimaryColor: isFeedbackRedesignEnabled ? isSelected : true,
        })}
        <Stack>
          {subTitle && (
            <Typography variant="caption" color="primary">
              {subTitle}
            </Typography>
          )}
          <Typography
            whiteSpace="break-spaces"
            color={isSelected ? 'primary.main' : 'inherit'}
            variant={getTypographyVariant()}
          >
            {label.displayName}
          </Typography>
        </Stack>
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        pr={isSelected || isClickable || shouldShowEditIcon ? 1 : 0}
      >
        {renderVisibilityIcon()}
        {renderEditIcon()}
      </Stack>
    </Stack>
  );
};
